/**
 * Constants to use for routes
 */
export const enum UnauthenticatedRoute {
  SIGN_IN = '/sign-in',
  SIGN_UP = '/sign-up',
  VERIFY_CODE = '/verify-code',
  UNSUBSCRIBE = '/unsubscribe',
}

export const enum EndUserRoute {
  HOME = '/home',
  LEARNING = '/learning',
  LEARNING_RECOMMENDED = '/learning/recommended',
  LEARNING_SAVED = '/learning/saved',
  LEARNING_SEARCH = '/learning/search',
  RESOURCES = '/resources',
  RESOURCES_RECOMMENDED = '/resources/recommended',
  RESOURCES_SAVED = '/resources/saved',
  RESOURCES_SEARCH = '/resources/search',
  CAREERS = '/careers',
  WORK_RECOMMENDED = '/work/recommended',
  WORK_FIND_JOBS = '/work/find-jobs',
  WORK_MY_JOBS = '/work/my-jobs',
  WORK_JOB_TRACKER = '/work/job-tracker',
  CAREERS_BROWSE = '/careers/browse',
  CAREERS_SAVED = '/careers/saved',
  CAREERS_MY_CAREER_PATH = '/careers/my-career-path',
  ROADMAP = '/roadmap',
  ROADMAP_UPCOMING = '/roadmap/upcoming',
  ROADMAP_COMPLETED = '/roadmap/completed',
  ROADMAP_ALL = '/roadmap/all',
  ACCOUNT = '/account',
  PASSPORT = '/my-career-passport',
  PASSPORT_EXPERIENCE = '/my-career-passport/experience',
  PASSPORT_SKILLS = '/my-career-passport/skills',
  PASSPORT_INTERESTS = '/my-career-passport/interests',
  PASSPORT_PERSONAL_INFO = '/my-career-passport/personal-information',
}

const enum InsightsRoutes {
  INSIGHTS = '/insights',
  INSIGHTS_USERS = '/insights/users',
  INSIGHTS_ACTIVITY = '/insights/activity',
  INSIGHTS_CUSTOM = '/insights/custom',
}

export const enum AdminRoutes {
  BRANDING = '/branding',
  BUSINESS_SERVICES = '/business-services',
  BUSINESS_SERVICES_TALENT = '/business-services/talent',
  CAREERS_CONFIG = '/careers-config',
  CREATE_MANUAL_JOB = '/business-services/jobs/manual',
  SIGN_UP_FORM = '/sign-up-form',
  DEFAULT_FORM_QUESTIONS = '/default-form-questions',
  FORMS = '/forms',
  LEARNING_CONFIG = '/learning-config',
  MANAGE_COACHES = '/manage/coaches',
  MANAGE_EMPLOYERS = '/business-services/employers',
  MANAGE_GROUPS = '/groups',
  MANAGE_INVITES = '/manage/invites',
  MANAGE_JOBS = '/business-services/jobs',
  MANAGE_RECRUITERS = '/business-services/recruiters',
  MANAGE_REPORTS = '/manage/reports',
  MANAGE_TAGS = '/manage/tags',
  MANAGE_CONTENT = '/manage-content',
  MANAGE_CONTENT_LEARNING = '/manage-content/learning',
  MANAGE_CONTENT_LEARNING_COURSES = '/manage-content/learning/courses',
  MANAGE_CONTENT_LEARNING_PROVIDERS = '/manage-content/learning/providers',
  MANAGE_CONTENT_LEARNING_NEW = '/manage-content/learning/new',
  MANAGE_CONTENT_LEARNING_EDIT = '/manage-content/learning/edit',
  MANAGE_CONTENT_LEARNING_RESTORE = '/manage-content/learning/restore',
  MANAGE_CONTENT_RESOURCES = '/manage-content/resources',
  MANAGE_CONTENT_RESOURCES_PROVIDERS = '/manage-content/resources/providers',
  MANAGE_CONTENT_RESOURCES_ALL_RESOURCES = '/manage-content/resources/all',
  RESOURCES_CONFIG = '/resources-config',
  YOUR_ROADMAP_DRAFT = '/your-roadmap/draft',
  YOUR_ROADMAP_PUBLISHED = '/your-roadmap/published',
  CONFIGURATIONS_LEARNING = '/configurations/learning',
  CONFIGURATIONS_LEARNING_COURSES = '/configurations/learning/courses',
  CONFIGURATIONS_RESOURCES = '/configurations/resources',
  CONFIGURATIONS_CAREERS = '/configurations/careers',
  CONFIGURATIONS_BRANDING = '/configurations/branding',
  CONFIGURATIONS_WORK = '/configurations/work',
  CONFIGURATIONS_ROADMAP = '/configurations/roadmap',
  CONFIGURATIONS_ROADMAP_DRAFT = '/configurations/roadmap/draft',
  CONFIGURATIONS_ROADMAP_PUBLISHED = '/configurations/roadmap/published',
  CONFIGURATIONS_FORMS = '/configurations/forms',
  CONFIGURATIONS_REGISTRATION = '/configurations/registration',
  CONFIGURATIONS_INSIGHTS = '/configurations/insights',
  CONFIGURATIONS_BUSINESS_SERVICES = '/configurations/business-services',
  CONFIGURATIONS_GROUPS = '/configurations/coach-permissions',
  CONFIGURATIONS = '/configurations',
  PAGE_NOT_FOUND = '/page-not-found',
  INSIGHTS = InsightsRoutes.INSIGHTS,
  INSIGHTS_USERS = InsightsRoutes.INSIGHTS_USERS,
  INSIGHTS_ACTIVITY = InsightsRoutes.INSIGHTS_ACTIVITY,
  INSIGHTS_CUSTOM = InsightsRoutes.INSIGHTS_CUSTOM,
  BUSINESS_RESOURCES = '/business-resources',
  MANAGE_CONTENT_BUSINESS_RESOURCES = '/manage-content/business-resources',
  MANAGE_CONTENT_BUSINESS_RESOURCES_PROVIDERS = '/manage-content/business-resources/providers',
  MANAGE_CONTENT_BUSINESS_RESOURCES_ALL_RESOURCES = '/manage-content/business-resources/all',
  MANAGE_CONTENT_BUSINESS_RESOURCES_NEW = '/manage-content/business-resources/new',
  MANAGE_CONTENT_BUSINESS_RESOURCES_EDIT = '/manage-content/business-resources/edit',
  CONFIGURATIONS_BUSINESS_RESOURCES = '/configurations/business-resources',
  CONFIGURATIONS_2FA = '/configurations/2fa',
  CONFIGURATIONS_PLATFORM = '/configurations/platform',
}

export const enum CoachRoutes {
  MANAGE = '/manage',
  INSIGHTS = InsightsRoutes.INSIGHTS,
  INSIGHTS_USERS = InsightsRoutes.INSIGHTS_USERS,
  INSIGHTS_ACTIVITY = InsightsRoutes.INSIGHTS_ACTIVITY,
  INSIGHTS_CUSTOM = InsightsRoutes.INSIGHTS_CUSTOM,
}

export const enum RecruiterRoutes {
  MANAGE_JOBS = '/manage/jobs',
  CREATE_MANUAL_JOB = '/manage/jobs/manual',
  FIND_TALENT = '/search/talent',
  ACCOUNT = '/account',
  PAGE_NOT_FOUND = '/page-not-found',
  INSIGHTS = InsightsRoutes.INSIGHTS,
  INSIGHTS_USERS = InsightsRoutes.INSIGHTS_USERS,
  INSIGHTS_ACTIVITY = InsightsRoutes.INSIGHTS_ACTIVITY,
  INSIGHTS_CUSTOM = InsightsRoutes.INSIGHTS_CUSTOM,
}

export const enum RoadmapRoutes {
  UPCOMING = '/roadmap/upcoming',
  COMPLETED = '/roadmap/completed',
  ALL = '/roadmap/all',
}

export const enum FormRoutes {
  NEW_FORM = '/forms/new',
}

export const sharedJobLinkRoute = '/job-link';
export const UserRecommendedJobsRoute = '/recommended/work'; // Route shared by Coaches & Admins when they are in Manage User > User > Recommended > Work tab.

export const generateJobLink = ({
  tenantCode,
  jobId,
  destinationRoute,
}: {
  tenantCode: string;
  jobId: string;
  destinationRoute?: string;
}) => {
  if (destinationRoute) {
    return `${window.location.origin}${destinationRoute}?tc=${tenantCode}`;
  }
  return `${window.location.origin}${sharedJobLinkRoute}/${jobId}?tc=${tenantCode}`;
};
